/**
 * Hooks TanStack Query — printable_specs V4 (bascule Gelato 2026-05-17).
 *
 * Modèle générique : 1 endpoint create unique `(provider_slug, sku, attributes)`.
 * Plus de discriminant `kind` (`art_print` / `framed_print`). Pour ajouter
 * une autre taille à une spec existante, utiliser `useAddPrintableSpecVariant`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  printableSpecsApi,
  type AddVariantRequest,
  type ListPrintableSpecsParams,
  type PrintableSpecCreateRequest,
  type PrintableSpecListResponse,
  type PrintableSpecResponse,
  type PrintableSpecUpdateRequest,
} from '@/utils/api/printable-specs'

export const printableSpecsKeys = {
  all: ['printable-specs'] as const,
  lists: () => [...printableSpecsKeys.all, 'list'] as const,
  list: (params: ListPrintableSpecsParams) => [...printableSpecsKeys.lists(), params] as const,
  picker: (provider_slug: string) => [...printableSpecsKeys.all, 'picker', provider_slug] as const,
  detail: (id: number) => [...printableSpecsKeys.all, 'detail', id] as const,
}

// ──────────────────────────────────────────────────────────────────────────────
// Queries
// ──────────────────────────────────────────────────────────────────────────────

export function usePrintableSpecs(params: ListPrintableSpecsParams = {}) {
  return useQuery<PrintableSpecListResponse>({
    queryKey: printableSpecsKeys.list(params),
    queryFn: () => printableSpecsApi.list(params),
  })
}

export function usePrintableSpecsPicker(provider_slug = 'gelato') {
  return useQuery<PrintableSpecResponse[]>({
    queryKey: printableSpecsKeys.picker(provider_slug),
    queryFn: () => printableSpecsApi.picker(provider_slug),
    staleTime: 30_000,
  })
}

export function usePrintableSpec(id: number | null) {
  return useQuery<PrintableSpecResponse>({
    queryKey: printableSpecsKeys.detail(id ?? -1),
    queryFn: () => printableSpecsApi.get(id as number),
    enabled: id !== null && id > 0,
  })
}

// ──────────────────────────────────────────────────────────────────────────────
// Mutations
// ──────────────────────────────────────────────────────────────────────────────

export function useCreatePrintableSpec() {
  const qc = useQueryClient()
  return useMutation<
    PrintableSpecResponse,
    { detail: unknown; status?: number },
    PrintableSpecCreateRequest
  >({
    mutationFn: (payload) => printableSpecsApi.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: printableSpecsKeys.all })
    },
  })
}

export function useAddPrintableSpecVariant(specId: number) {
  const qc = useQueryClient()
  return useMutation<
    PrintableSpecResponse,
    { detail: unknown; status?: number },
    AddVariantRequest
  >({
    mutationFn: (payload) => printableSpecsApi.addVariant(specId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: printableSpecsKeys.all })
    },
  })
}

export function useUpdatePrintableSpec(specId: number) {
  const qc = useQueryClient()
  return useMutation<
    PrintableSpecResponse,
    { detail: unknown; status?: number },
    PrintableSpecUpdateRequest
  >({
    mutationFn: (payload) => printableSpecsApi.update(specId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: printableSpecsKeys.all })
    },
  })
}

export function useArchivePrintableSpec() {
  const qc = useQueryClient()
  return useMutation<void, { detail: unknown; status?: number }, number>({
    mutationFn: (id) => printableSpecsApi.archive(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: printableSpecsKeys.all })
    },
  })
}

export function useDeletePrintableSpec() {
  const qc = useQueryClient()
  return useMutation<void, { detail: unknown; status?: number }, number>({
    mutationFn: (id) => printableSpecsApi.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: printableSpecsKeys.all })
    },
  })
}
